/** A ranger's perks, traits and fatigue as the `SimMods` the simulation reads (04 §4.2-§4.4). */

import { defaultMods } from '../sim/mods';
import type { MissionParams, SimMods } from '../sim/types';
import { inStorm } from '../sim/weather';
import { FATIGUE } from './content/economy';
import { type ModEffect, type NumericModKey, PERKS, WEATHER_PENALTY } from './content/perks';
import { TRAIT_RULES, TRAITS } from './content/traits';
import type { RangerRecord } from './types';

function apply(mods: SimMods, effect: ModEffect, params: MissionParams | null): void {
  for (const key of Object.keys(effect.mul ?? {}) as NumericModKey[]) mods[key] *= effect.mul?.[key] ?? 1;
  for (const key of Object.keys(effect.add ?? {}) as NumericModKey[]) mods[key] += effect.add?.[key] ?? 0;
  for (const flag of effect.flags ?? []) mods[flag] = true;
  // Only at home: Cold Blooded is no use in a desert sandstorm, whatever the weather is doing.
  if (params && effect.weatherProof === params.biome) {
    // No SimMods field for fatigue rate: a larger pool cancels a faster-filling bar exactly, because
    // ranger/movement.ts divides the fill rate by the pool and multiplies it by these very penalties.
    if (params.biome === 'desert') mods.staminaPool *= 1 + WEATHER_PENALTY.desertHeat;
    // R5: `inStorm` is the sim's own test, so the bonus is paid for exactly the drain that is charged.
    if (inStorm(params)) mods.staminaPool *= 1 + WEATHER_PENALTY.storm;
  }
}

/** Carry units a ranger's perks and traits add to the personal cap (needed before any mission is chosen). */
export function carryBonus(ranger: RangerRecord): number {
  return modsFor(ranger, null, false).carryBonus;
}

/**
 * The full modifier set for one deployment. `params` lets biome-specific perks see where the
 * mission is; `luckyAvailable` is false once the Lucky charge is spent for this operation.
 */
export function modsFor(ranger: RangerRecord, params: MissionParams | null, luckyAvailable: boolean): SimMods {
  const mods = defaultMods();
  for (const id of ranger.perks) {
    const perk = PERKS.find((p) => p.id === id);
    if (perk) apply(mods, perk.effect, params);
  }
  const blooded = ranger.traits.includes('blooded');
  for (const id of ranger.traits) {
    if (blooded && TRAIT_RULES.bloodedCancels.includes(id)) continue;
    const trait = TRAITS.find((t) => t.id === id);
    if (trait) apply(mods, trait.effect, params);
  }
  if (ranger.traits.includes('lucky') && luckyAvailable) mods.luckyCharges = 1;
  const tired = ranger.fatigue - FATIGUE.freePoints;
  if (tired > 0) mods.staminaPool *= Math.max(FATIGUE.floor, 1 - FATIGUE.perPoint * tired);
  return mods;
}
